import type { ReactNode } from 'react';
import Box, { type BoxProps } from '@mui/material/Box';
import { v } from './format';

/** Page head: title, subtitle and right-aligned actions. */
export function PageHeader({ title, subtitle, actions }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: '22px' }}>
      <Box sx={{ minWidth: 0 }}>
        <Box component="h1" sx={{ m: 0, fontSize: { xs: 22, md: 27 }, fontWeight: 700, letterSpacing: '-0.025em', color: v('text') }}>
          {title}
        </Box>
        {subtitle && <Box sx={{ mt: '6px', fontSize: 14, color: v('dim') }}>{subtitle}</Box>}
      </Box>
      {actions && <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>{actions}</Box>}
    </Box>
  );
}

export function Page({ children }: { children: ReactNode }) {
  return <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>{children}</Box>;
}

/** Card: panel surface, 16px radius. Title row with optional subtitle and action. */
export function Card({
  title, subtitle, action, children, padding = '20px 22px', flush = false, sx, ...rest
}: { title?: ReactNode; subtitle?: ReactNode; action?: ReactNode; children?: ReactNode; padding?: string; flush?: boolean } & Omit<BoxProps, 'title'>) {
  return (
    <Box
      component="section"
      sx={{ background: v('panel'), border: `1px solid ${v('border')}`, borderRadius: '16px', overflow: 'hidden', minWidth: 0, ...sx }}
      {...rest}
    >
      {(title || action) && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, p: flush ? '18px 22px 12px' : padding, pb: flush ? '12px' : 0 }}>
          <Box sx={{ minWidth: 0 }}>
            {title && <Box component="h2" sx={{ m: 0, fontSize: 15, fontWeight: 600, color: v('text') }}>{title}</Box>}
            {subtitle && <Box sx={{ mt: '4px', fontSize: 12.5, color: v('caption') }}>{subtitle}</Box>}
          </Box>
          {action && <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>{action}</Box>}
        </Box>
      )}
      <Box sx={{ p: flush ? 0 : padding, pt: title || action ? (flush ? 0 : '14px') : undefined }}>{children}</Box>
    </Box>
  );
}

/** Responsive column grid. `cols` are CSS grid track sizes for desktop; collapses to one column on phones. */
export function Cols({ cols, gap = 14, children, sx }: { cols: string; gap?: number; children: ReactNode; sx?: BoxProps['sx'] }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: cols }, gap: `${gap}px`, alignItems: 'start', ...sx }}>
      {children}
    </Box>
  );
}

/** Summary strip: equal cells divided by hairlines (Transactions, Payouts, Disputes …). */
export function Strip({ cells }: { cells: { label: ReactNode; value: ReactNode; foot?: ReactNode; tone?: string }[] }) {
  return (
    <Box
      sx={{
        display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0,1fr))', md: `repeat(${cells.length}, minmax(0,1fr))` },
        background: v('panel'), border: `1px solid ${v('border')}`, borderRadius: '16px', overflow: 'hidden',
      }}
    >
      {cells.map((c, i) => (
        <Box key={i} sx={{ p: '16px 18px', borderLeft: { md: i ? `1px solid ${v('divider')}` : 0 } }}>
          <Box sx={{ fontSize: 12, color: v('caption'), fontWeight: 500 }}>{c.label}</Box>
          <Box className="ix-mono" sx={{ mt: '6px', fontSize: 18, fontWeight: 600, color: c.tone ? v(c.tone) : v('text') }}>{c.value}</Box>
          {c.foot && <Box sx={{ mt: '4px', fontSize: 12, color: v('dim') }}>{c.foot}</Box>}
        </Box>
      ))}
    </Box>
  );
}

/** KPI tile with optional sparkline slot. */
export function KpiTile({ label, value, foot, children }: { label: ReactNode; value: ReactNode; foot?: ReactNode; children?: ReactNode }) {
  return (
    <Box sx={{ background: v('panel'), border: `1px solid ${v('border')}`, borderRadius: '16px', p: '16px 18px', minWidth: 0 }}>
      <Box sx={{ fontSize: 12.5, color: v('caption'), fontWeight: 500, display: 'flex', alignItems: 'center', gap: 1 }}>{label}</Box>
      <Box className="ix-mono" sx={{ mt: '8px', fontSize: 26, fontWeight: 600, letterSpacing: '-0.02em', color: v('text') }}>{value}</Box>
      {foot && <Box sx={{ mt: '6px', fontSize: 12, color: v('dim') }}>{foot}</Box>}
      {children && <Box sx={{ mt: '10px' }}>{children}</Box>}
    </Box>
  );
}

export function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <Box sx={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: v('caption') }}>{children}</Box>
  );
}

/** Label/value rows for detail side cards. */
export function KeyValues({ rows }: { rows: { k: ReactNode; v: ReactNode }[] }) {
  return (
    <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: 'minmax(110px, auto) minmax(0,1fr)', rowGap: '10px', columnGap: 2, fontSize: 13 }}>
      {rows.map((r, i) => (
        <Box key={i} sx={{ display: 'contents' }}>
          <Box component="dt" sx={{ color: v('caption') }}>{r.k}</Box>
          <Box component="dd" sx={{ m: 0, color: v('text'), textAlign: 'right', minWidth: 0 }}>{r.v}</Box>
        </Box>
      ))}
    </Box>
  );
}
